import React from 'react';
import { Checkbox, Flex, Tag, Typography, theme } from 'antd';

import { CustomTooltip } from '~components/Tooltip';

import type { GscProperty } from '../api/gscConnectionApi';
import { GSC_PICKER_CAP_HELP_AT_CAP, GSC_SAME_DOMAIN_BLOCKED_COPY } from './GscActivationErrors';

const SYNCED_TAG = 'synced';
const ACCESS_LOST_TAG = 'Access lost';
const ACCESS_LOST_COPY = {
  unlisted:
    'Google Search Console no longer shares this property with the connected account. Untick it to remove it from Search Performance, or add it back in Search Console and reopen this page to sync it again.',
  relisted:
    'Search Console access to this property was lost and is available again. Tick it to sync it again, or leave it unticked to remove it.',
};

type Props = {
  property: GscProperty;
  checked: boolean;
  disabled: boolean;
  isUnsyncable: boolean;
  isDomainBlocked: boolean;
  /** Unticked while the plan cap is spent — the only disabled state with no text under the row. */
  isCapBlocked: boolean;
  /** This property is already syncing upstream, not a pending selection. */
  isSynced?: boolean;
  /** `unlisted`: Google no longer lists the lost property. `relisted`: Google lists it again after the loss. */
  accessLost?: 'unlisted' | 'relisted';
  onToggle: () => void;
};

function GscPropertyPickerRow({
  property,
  checked,
  disabled,
  isUnsyncable,
  isDomainBlocked,
  isCapBlocked,
  isSynced,
  accessLost,
  onToggle,
}: Props) {
  const { token } = theme.useToken();
  const accessLostHint =
    accessLost === 'unlisted' || (accessLost === 'relisted' && !checked) ? ACCESS_LOST_COPY[accessLost] : null;

  return (
    <Flex vertical gap={token.marginXXS}>
      <CustomTooltip
        placement="top"
        message={isCapBlocked ? GSC_PICKER_CAP_HELP_AT_CAP : undefined}
        wrapperStyle={{ display: 'block' }}
      >
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          style={{
            width: '100%',
            alignItems: 'center',
            padding: token.padding,
            borderRadius: token.borderRadiusLG,
            border: `${token.lineWidth}px ${token.lineType} ${
              checked ? token.colorPrimary : token.colorBorderSecondary
            }`,
            background: checked ? token.colorPrimaryBg : token.colorBgContainer,
          }}
        >
          {property.siteUrl}
          {property.recommended === true && (
            <Tag
              bordered={false}
              style={{
                marginLeft: token.marginXS,
                color: token.colorPrimary,
                background: token.colorPrimaryBg,
              }}
            >
              Recommended
            </Tag>
          )}
          {isSynced === true && (
            // Inside the checkbox label so a screen reader announces the badge.
            <Tag bordered={false} style={{ marginLeft: token.marginXS }}>
              {SYNCED_TAG}
            </Tag>
          )}
          {accessLost === 'unlisted' && (
            <Tag bordered={false} color="warning" style={{ marginLeft: token.marginXS }}>
              {ACCESS_LOST_TAG}
            </Tag>
          )}
        </Checkbox>
      </CustomTooltip>
      {isUnsyncable && (
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Needs Owner or Full access in Search Console — this account has read-restricted access, which cannot return
          performance data.
        </Typography.Text>
      )}
      {!isUnsyncable && isDomainBlocked && (
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {GSC_SAME_DOMAIN_BLOCKED_COPY}
        </Typography.Text>
      )}
      {!isUnsyncable && !isDomainBlocked && accessLostHint && (
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {accessLostHint}
        </Typography.Text>
      )}
    </Flex>
  );
}

export default GscPropertyPickerRow;
